import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid2,
  List,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import FeedbackDialogListener from '@components/Modal/Content/FeedbackModalContent/FormDialog';
import { DelayedSearchTextInput } from '@components/withDelayedSearch/DelayedSearchTextInput';

import {
  useGetDirectEMRIntegrations,
  useGetEMRConnections,
} from '@hooks/api/emr-connections';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import PaginationFooter from '@views/Admin/NewAccount/ImportJobList/PaginationFooter';

import {
  FeedbackEmailType,
  type DirectEmrApiEndpoint,
  type GetEmrIntegrationsInput,
} from 'generated/types';

import EMRIntegration from './EMRIntegration';

// import FilterButton from './FilterButton';

const messages = defineMessages({
  emrConnections: {
    id: 'connectionsMain.emrConnections',
    defaultMessage: 'Hospitals and Clinics (EMRs)',
  },
  myEmrConnections: {
    id: 'connectionsMain.myEmrConnections',
    defaultMessage: 'My Hospitals and Clinics',
  },
  statusNoEmrConnections: {
    id: 'connectionsMain.statusNoEmrConnections',
    defaultMessage: 'No hospitals or clinics connected',
  },
  statusNoEmrIntegrations: {
    id: 'connectionsMain.statusNoEmrIntegrations',
    defaultMessage: 'No hospitals or clinics to connect',
  },
  addEMRConnection: {
    id: 'connectionsMain.addEMRConnections',
    defaultMessage: 'Hospital and Clinics',
  },
  search: {
    id: 'connectionsMain.search',
    defaultMessage: 'Search by name',
  },
  clinicNotFound: {
    id: 'connectionsMain.noClinicFound',
    defaultMessage: 'Don’t see your hospital/clinic?',
  },
});

function EMRConnections() {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const isMobile = useIsMobile();

  const {
    data: emrIntegrationsData,
    loading: emrIntegrationsLoading,
    refetch,
  } = useGetDirectEMRIntegrations();
  const { data: emrConnectionsData, loading: emrConnectionsLoading } =
    useGetEMRConnections({
      vitall_patient_id: activeProfile?.vitall_patient_id ?? '',
    });

  const loading = emrConnectionsLoading || emrIntegrationsLoading;

  const [filters, setFilters] = useState<
    Omit<GetEmrIntegrationsInput, 'query' | 'vitall_patient_id'>
  >({
    page: 0,
    count: 25,
  });
  const [emrEndpoints, setEmrEndpoints] = useState<DirectEmrApiEndpoint[]>(
    emrIntegrationsData ?? []
  );

  const handleSearch = async (value: string) => {
    const { data } = await refetch({
      input: {
        ...filters,
        query: value,
        page: 0,
      },
    });
    const integrations = data.listDirectEmrApiIntegrations;
    setEmrEndpoints(integrations);
  };

  useEffect(() => {
    async function fetchFilteredIntegrations() {
      const { data } = await refetch({
        input: {
          ...filters,
        },
      });
      const integrations = data.listDirectEmrApiIntegrations;
      setEmrEndpoints(integrations);
    }
    fetchFilteredIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile?.vitall_patient_id, filters]);

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const unconnectedEmrEndpoints = emrEndpoints.filter(
    ({ fhir_api_endpoint_id }) =>
      !emrConnectionsData?.find(
        ({ fhir_api_endpoint_id: _faeid }) => _faeid === fhir_api_endpoint_id
      )
  );

  return (
    <Container
      maxWidth="md"
      sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
    >
      <Card>
        <CardHeader title={formatMessage(messages.myEmrConnections)} />
        <Divider />
        <ContentLoader loading={loading}>
          {emrConnectionsData?.length ? (
            <List>
              {emrConnectionsData.map((connection, index) => {
                const integration = emrIntegrationsData?.find(
                  (integration) =>
                    integration.fhir_api_endpoint_id ===
                    connection.fhir_api_endpoint_id
                );
                if (integration) {
                  return (
                    <React.Fragment
                      key={`emr-connection-${connection.fhir_api_endpoint_id}`}
                    >
                      <EMRIntegration
                        connection={connection}
                        integration={integration}
                      />
                      {index < emrConnectionsData.length - 1 ? (
                        <Divider />
                      ) : null}
                    </React.Fragment>
                  );
                }
                return null;
              })}
            </List>
          ) : (
            <CardContent>
              {formatMessage(messages.statusNoEmrConnections)}
            </CardContent>
          )}
        </ContentLoader>
      </Card>

      <Card>
        <CardHeader
          action={
            <Grid2
              sx={{
                display: 'flex',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {/* <FilterButton changeFilters={setFilters} filters={filters} /> */}
              <DelayedSearchTextInput
                onSearch={handleSearch}
                placeholder={formatMessage(messages.search)}
              />
            </Grid2>
          }
          subheader={
            <FeedbackDialogListener type={FeedbackEmailType.Emr_Connection}>
              {({ handleOpen }) => (
                <Typography
                  color="info.main"
                  onClick={handleOpen}
                  sx={{ cursor: 'pointer' }}
                  variant="body2"
                >
                  {formatMessage(messages.clinicNotFound)}
                </Typography>
              )}
            </FeedbackDialogListener>
          }
          title={formatMessage(messages.addEMRConnection)}
        />
        <Divider />
        <ContentLoader loading={loading}>
          {unconnectedEmrEndpoints.length ? (
            <List>
              {unconnectedEmrEndpoints.map((emrIntegration, index) => {
                const { fhir_api_endpoint_id } = emrIntegration;

                return (
                  <React.Fragment key={fhir_api_endpoint_id}>
                    <EMRIntegration integration={emrIntegration} />
                    {index < unconnectedEmrEndpoints.length - 1 ? (
                      <Divider />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <CardContent>
              {formatMessage(messages.statusNoEmrIntegrations)}
            </CardContent>
          )}
        </ContentLoader>
        {unconnectedEmrEndpoints.length > 0 && (
          <CardActions>
            <table>
              <PaginationFooter
                changePage={handlePageChange}
                page={filters.page ?? 0}
              />
            </table>
          </CardActions>
        )}
      </Card>
    </Container>
  );
}
export default withErrorBoundary(EMRConnections, {
  FallbackComponent: (props) => (
    <Container maxWidth="md">
      <ErrorBoundaryFallback {...props} />
    </Container>
  ),
});
